import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { isValidLegislationNumber } from "@repo/shared/legislationNumbering";
import type { Document, Tenant } from "@repo/types";
import { useEffect, useRef, useState } from "react";
import { apiService } from "@/services/apiService";
import {
  getDocumentLegislationNumberFormat,
  type InvalidLegislationNumber,
  type LegislationNumberField,
  type ReservedLegislationNumber,
} from "./types";

export const useLegislationNumberReservation = ({
  initialValues,
  tenant,
  tenantId,
}: {
  initialValues: Pick<Document, "ordinanceData" | "resolutionData">;
  tenant?: Tenant | null;
  tenantId: string;
}) => {
  const reservedNumberRef = useRef<ReservedLegislationNumber | null>(null);
  const operationRef = useRef<Promise<void>>(Promise.resolve());
  const lastValidNumberRef = useRef<Record<LegislationNumberField, string>>({
    draftNumber: "",
    ordinanceNumber: "",
    resolutionNumber: "",
  });
  const [invalidNumber, setInvalidNumber] =
    useState<InvalidLegislationNumber | null>(null);

  useEffect(() => {
    lastValidNumberRef.current = {
      draftNumber: initialValues.ordinanceData?.draftNumber ?? "",
      ordinanceNumber: initialValues.ordinanceData?.number ?? "",
      resolutionNumber: initialValues.resolutionData?.number ?? "",
    };
  }, [
    initialValues.ordinanceData?.draftNumber,
    initialValues.ordinanceData?.number,
    initialValues.resolutionData?.number,
  ]);

  const rememberValidNumber = useFutureCallback(
    (field: LegislationNumberField, number: string) => {
      lastValidNumberRef.current[field] = number;
    }
  );

  const queueOperation = useFutureCallback((operation: () => Promise<void>) => {
    const nextOperation = operationRef.current
      .then(operation, operation)
      .catch(() => undefined);

    operationRef.current = nextOperation;

    return nextOperation;
  });

  const releaseReservedNumber = useFutureCallback(
    (field: LegislationNumberField, nextValue: string) => {
      const reservedNumber = reservedNumberRef.current;
      if (
        !reservedNumber ||
        reservedNumber.committed ||
        reservedNumber.field !== field ||
        reservedNumber.number === nextValue
      ) {
        return;
      }

      reservedNumberRef.current = null;
      void queueOperation(async () => {
        await apiService.v1.revertLegislationNumber(
          tenantId,
          reservedNumber.type,
          {
            number: reservedNumber.number,
          }
        );
      });
    }
  );

  const validateNumber = useFutureCallback(
    (field: LegislationNumberField, value: string) => {
      const format = getDocumentLegislationNumberFormat(field, tenant);

      if (isValidLegislationNumber(value, format)) {
        rememberValidNumber(field, value);
        return;
      }

      setInvalidNumber({
        field,
        previousValue: lastValidNumberRef.current[field],
        format,
      });
    }
  );

  const markReservedNumberCommitted = useFutureCallback(() => {
    reservedNumberRef.current = reservedNumberRef.current
      ? { ...reservedNumberRef.current, committed: true }
      : null;
  });

  return {
    invalidNumber,
    markReservedNumberCommitted,
    queueOperation,
    releaseReservedNumber,
    rememberValidNumber,
    reservedNumberRef,
    setInvalidNumber,
    validateNumber,
  };
};
